import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { GripVertical, FileText, Plus, Trash2, X, Info } from 'lucide-react';
import { toast } from 'sonner';

// Sortable Item Component
function SortableSheetItem({ sheet, toggleSheet, removeSheet }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sheet.sheet_number });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`p-4 transition-all border border-slate-200 dark:border-slate-700 ${
        sheet.included
          ? 'bg-white dark:bg-slate-800 shadow-sm'
          : 'bg-slate-50 dark:bg-slate-900/50 opacity-60'
      } ${isDragging ? 'shadow-lg ring-2 ring-blue-500 opacity-50' : ''}`}
      data-testid={`sheet-editor-${sheet.sheet_number}`}
    >
      <div className="flex items-center space-x-4">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5 text-slate-400" />
        </div>

        <Checkbox
          checked={sheet.included}
          onCheckedChange={() => toggleSheet(sheet.sheet_number)}
          data-testid={`checkbox-editor-${sheet.sheet_number}`}
        />

        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <span className="font-mono text-sm font-semibold text-blue-600">
              {sheet.sheet_number}
            </span>
            <span className="text-slate-900 dark:text-white font-medium">
              {sheet.title}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {sheet.sheet_type.replace('_', ' ').toUpperCase()}
          </p>
        </div>

        {sheet.content_blocks && sheet.content_blocks.length > 0 && (
          <span className="text-xs text-slate-500">
            {sheet.content_blocks.length} blocks
          </span>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => removeSheet(sheet.sheet_number)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid={`remove-editor-${sheet.sheet_number}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

const SheetIndexEditor = ({ project, onChange }) => {
  const sheets = project.sheets || [];
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [newSheet, setNewSheet] = useState({
    sheet_number: '',
    title: '',
    sheet_type: 'general',
    included: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const toggleSheet = (sheetNumber) => {
    onChange({
      ...project,
      sheets: sheets.map(sheet =>
        sheet.sheet_number === sheetNumber
          ? { ...sheet, included: !sheet.included }
          : sheet
      )
    });
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = sheets.findIndex((s) => s.sheet_number === active.id);
      const newIndex = sheets.findIndex((s) => s.sheet_number === over.id);

      const reorderedSheets = arrayMove(sheets, oldIndex, newIndex).map((sheet, index) => ({
        ...sheet,
        order: index,
      }));

      onChange({ ...project, sheets: reorderedSheets });
      toast.success('Sheet order updated');
    }
  };
  
  const addNewSheet = () => {
    if (!newSheet.sheet_number || !newSheet.title) {
      toast.error('Please fill in sheet number and title');
      return;
    }
    
    // Check if sheet number already exists
    if (sheets.some(s => s.sheet_number === newSheet.sheet_number)) {
      toast.error('Sheet number already exists');
      return;
    }
    
    const sheetToAdd = {
      ...newSheet,
      order: sheets.length,
      content_blocks: [],
    };
    
    onChange({ ...project, sheets: [...sheets, sheetToAdd] });
    setNewSheet({ sheet_number: '', title: '', sheet_type: 'general', included: true });
    setShowAddSheet(false);
    toast.success('Sheet added successfully');
  };
  
  const removeSheet = (sheetNumber) => {
    const updatedSheets = sheets.filter(s => s.sheet_number !== sheetNumber);
    onChange({ ...project, sheets: updatedSheets });
    toast.success('Sheet removed');
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Sheet Index
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Select which sheets to include in your project documentation
        </p>
      </div>
      
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-slate-200 dark:border-slate-700">
        <div className="flex items-start space-x-3">
          <Info className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
              Intelligent Auto-Pagination
            </h4>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              The system will automatically calculate how many pages each section needs. If content does not fit on one page, additional pages will be created (e.g., G0.00, G0.01, G0.02).
            </p>
          </div>
        </div>
      </Card>
      
      {/* Add Sheet Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setShowAddSheet(!showAddSheet)}
          data-testid="add-sheet-btn-editor"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Sheet
        </Button>
      </div>
      
      {/* Add Sheet Form */}
      {showAddSheet && (
        <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Add New Sheet
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddSheet(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sheet_number_editor">Sheet Number</Label>
              <Input
                id="sheet_number_editor"
                value={newSheet.sheet_number}
                onChange={(e) => setNewSheet({ ...newSheet, sheet_number: e.target.value })}
                placeholder="e.g., A7.00"
              />
            </div>
            <div>
              <Label htmlFor="sheet_title_editor">Sheet Title</Label>
              <Input
                id="sheet_title_editor"
                value={newSheet.title}
                onChange={(e) => setNewSheet({ ...newSheet, title: e.target.value })}
                placeholder="e.g., DETAILS"
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4 space-x-2">
            <Button variant="outline" onClick={() => setShowAddSheet(false)}>
              Cancel
            </Button>
            <Button onClick={addNewSheet} className="bg-[#1e40af] hover:bg-[#1e3a8a]">
              Add
            </Button>
          </div>
        </Card>
      )}
      
      {sheets.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sheets.map((s) => s.sheet_number)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {sheets.map((sheet) => (
                <SortableSheetItem
                  key={sheet.sheet_number}
                  sheet={sheet}
                  toggleSheet={toggleSheet}
                  removeSheet={removeSheet}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <Card className="p-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            No sheets available
          </p>
        </Card>
      )}
      
      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <div>
          <p className="text-sm font-medium text-slate-900 dark:text-white">
            Total Sheets Selected
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            These will be included in your documentation
          </p>
        </div>
        <div className="text-3xl font-bold text-blue-600">
          {sheets.filter(s => s.included).length}
        </div>
      </div>
    </div>
  );
};

export default SheetIndexEditor;